import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from '@mui/material';
import { format, subMonths, subYears } from 'date-fns';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { KPICard } from './KPICard';
import { PerformanceTrendChart } from './PerformanceTrendChart';
import { HotelCategoryChart } from './HotelCategoryChart';
import { CurrentPercentileRankings } from './CurrentPercentileRankings';
import { PercentileRankingsOverTime } from './PercentileRankingsOverTime';
import { AirportTrafficChart } from './AirportTrafficChart';
import { AirportArrivalsByCountryChart } from './AirportArrivalsByCountryChart';
import { MonthlyPassengerChart } from './MonthlyPassengerChart';
import { useMarketPerformance, useMarketTimeSeries, useMarketRankings, useMarketAirportTraffic, useMarketAirportArrivalsByCountry, useMarketMonthlyPassengers } from '../../../hooks/useMarketAnalytics';

interface MarketPerformanceDashboardProps {
  marketId: number;
  marketName: string;
  marketCountry?: string;
}

type PeriodType = 'current' | '3_month' | '12_month' | 'ytd';
type TimeRangeType = '1_year' | '10_year' | '20_year';

export const MarketPerformanceDashboard: React.FC<MarketPerformanceDashboardProps> = ({
  marketId,
  marketName,
  marketCountry,
}) => {
  const [selectedPeriod, setSelectedPeriod] = React.useState<PeriodType>('current');
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangeType>('10_year');

  // Fetch performance data
  const { data: performanceData, isLoading: isLoadingPerformance, error: performanceError } = useMarketPerformance(
    marketId,
    { period: selectedPeriod }
  );

  // Calculate date range for time series based on selected time range
  const dateRange = useMemo(() => {
    const endDate = new Date();
    let startDate: Date;
    
    switch (selectedTimeRange) {
      case '10_year':
        startDate = subYears(endDate, 10);
        break;
      case '20_year':
        startDate = subYears(endDate, 20);
        break;
      default:
        startDate = subMonths(endDate, 12);
    }
    
    return {
      start: format(startDate, 'yyyy-MM-dd'),
      end: format(endDate, 'yyyy-MM-dd'),
    };
  }, [selectedTimeRange]);

  // Fetch time series data
  const { data: timeSeriesData, isLoading: isLoadingTimeSeries } = useMarketTimeSeries(
    marketId,
    dateRange.start,
    dateRange.end,
    {
      metrics: ['adr', 'occupancy', 'revpar'],
      // Don't filter by property type to get all classifications
    }
  );

  // Fetch market rankings data
  const { data: rankingsData, isLoading: isLoadingRankings } = useMarketRankings(marketId);

  // Fetch airport traffic data
  const { data: airportTrafficData, isLoading: isLoadingAirportTraffic } = useMarketAirportTraffic(marketId);

  // Fetch airport arrivals by country data
  const { data: airportArrivalsByCountryData, isLoading: isLoadingAirportArrivalsByCountry } = useMarketAirportArrivalsByCountry(marketId);

  // Fetch monthly passenger data
  const { data: monthlyPassengersData, isLoading: isLoadingMonthlyPassengers } = useMarketMonthlyPassengers(marketId);

  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: PeriodType | null
  ) => {
    if (newPeriod !== null) {
      setSelectedPeriod(newPeriod);
    }
  };

  // Get the "All" category data for KPI cards
  const allCategoryData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return null;
    
    const allData = performanceData.find(item => 
      item.classification === 'All'
    );
    
    return allData;
  }, [performanceData]);

  // Helper function to convert percentage changes
  const convertPercentage = (value: number | null | undefined): number | null => {
    return value !== null && value !== undefined ? value * 100 : null;
  };

  // Get current metrics based on selected period
  const currentMetrics = useMemo(() => {
    if (!allCategoryData) return null;
    
    switch (selectedPeriod) {
      case 'current':
        return {
          adr: allCategoryData.current?.adr ?? null,
          adr_change: convertPercentage(allCategoryData.current?.adr_chg_yoy),
          occupancy: allCategoryData.current?.occupancy !== null && allCategoryData.current?.occupancy !== undefined
            ? (allCategoryData.current.occupancy < 1 ? allCategoryData.current.occupancy * 100 : allCategoryData.current.occupancy)
            : null,
          occupancy_change: convertPercentage(allCategoryData.current?.occupancy_chg_yoy),
          revpar: allCategoryData.current?.revpar ?? null,
          revpar_change: convertPercentage(allCategoryData.current?.revpar_chg_yoy),
        };
      case '3_month':
        return {
          adr: allCategoryData.three_month?.adr ?? null,
          adr_change: convertPercentage(allCategoryData.three_month?.adr_change),
          occupancy: allCategoryData.three_month?.occupancy !== null && allCategoryData.three_month?.occupancy !== undefined
            ? (allCategoryData.three_month.occupancy < 1 ? allCategoryData.three_month.occupancy * 100 : allCategoryData.three_month.occupancy)
            : null,
          occupancy_change: convertPercentage(allCategoryData.three_month?.occupancy_change),
          revpar: allCategoryData.three_month?.revpar ?? null,
          revpar_change: convertPercentage(allCategoryData.three_month?.revpar_change),
        };
      case '12_month':
        return {
          adr: allCategoryData.twelve_month?.adr ?? null,
          adr_change: convertPercentage(allCategoryData.twelve_month?.adr_change),
          occupancy: allCategoryData.twelve_month?.occupancy !== null && allCategoryData.twelve_month?.occupancy !== undefined
            ? (allCategoryData.twelve_month.occupancy < 1 ? allCategoryData.twelve_month.occupancy * 100 : allCategoryData.twelve_month.occupancy)
            : null,
          occupancy_change: convertPercentage(allCategoryData.twelve_month?.occupancy_change),
          revpar: allCategoryData.twelve_month?.revpar ?? null,
          revpar_change: convertPercentage(allCategoryData.twelve_month?.revpar_change),
        };
      case 'ytd':
        return {
          adr: allCategoryData.ytd?.adr ?? null,
          adr_change: convertPercentage(allCategoryData.ytd?.adr_chg_yoy),
          occupancy: allCategoryData.ytd?.occupancy !== null && allCategoryData.ytd?.occupancy !== undefined
            ? (allCategoryData.ytd.occupancy < 1 ? allCategoryData.ytd.occupancy * 100 : allCategoryData.ytd.occupancy)
            : null,
          occupancy_change: convertPercentage(allCategoryData.ytd?.occupancy_chg_yoy),
          revpar: allCategoryData.ytd?.revpar ?? null,
          revpar_change: convertPercentage(allCategoryData.ytd?.revpar_chg_yoy),
        };
      default:
        return null;
    }
  }, [allCategoryData, selectedPeriod]);

  // Prepare category data for the bar chart
  const categoryChartData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];
    
    return performanceData.map(item => {
      const metrics = selectedPeriod === 'current' 
        ? item.current
        : selectedPeriod === '3_month'
        ? item.three_month
        : selectedPeriod === '12_month'
        ? item.twelve_month
        : item.ytd;

      return {
        category: item.classification,
        adr: metrics?.adr || 0,
        occupancy: metrics?.occupancy !== null && metrics?.occupancy !== undefined
          ? (metrics.occupancy < 1 ? metrics.occupancy * 100 : metrics.occupancy)
          : 0,
        revpar: metrics?.revpar || 0,
        adr_change: convertPercentage(
          selectedPeriod === 'current' || selectedPeriod === 'ytd' 
            ? metrics?.adr_chg_yoy 
            : (metrics as any)?.adr_change
        ) || 0,
        occupancy_change: convertPercentage(
          selectedPeriod === 'current' || selectedPeriod === 'ytd'
            ? metrics?.occupancy_chg_yoy 
            : (metrics as any)?.occupancy_change
        ) || 0,
        revpar_change: convertPercentage(
          selectedPeriod === 'current' || selectedPeriod === 'ytd'
            ? metrics?.revpar_chg_yoy 
            : (metrics as any)?.revpar_change
        ) || 0,
      };
    });
  }, [performanceData, selectedPeriod]);

  // Process time series data for chart
  const processedTimeSeriesData = useMemo(() => {
    if (!timeSeriesData || !Array.isArray(timeSeriesData) || timeSeriesData.length === 0) {
      return [];
    }
    
    // Group by date and aggregate "All" category
    const groupedData = timeSeriesData.reduce((acc: Record<string, {
      date: string;
      adr?: number;
      occupancy?: number;
      revpar?: number;
    }>, item) => {
      if (item.slice === 'All') {
        const date = item.date;
        if (!acc[date]) {
          acc[date] = { date };
        }
        acc[date].adr = item.adr;
        // Convert occupancy from decimal to percentage if needed
        acc[date].occupancy = item.occupancy && item.occupancy < 1 ? item.occupancy * 100 : item.occupancy;
        acc[date].revpar = item.revpar;
      }
      return acc;
    }, {});

    return Object.values(groupedData).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [timeSeriesData]);

  if (performanceError) {
    const errorMessage = performanceError instanceof Error && performanceError.message.includes('timed out')
      ? 'Market analytics data is taking longer than expected to load. This market may have a large dataset. Please try again in a few moments.'
      : 'Failed to load market performance data. Please try again later.';
    
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {errorMessage}
      </Alert>
    );
  }

  // Show loading state while data is being fetched
  if (isLoadingPerformance && !performanceData) {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <ShowChartIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Market Performance Analytics
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading market analytics data...</Typography>
        </Box>
      </Paper>
    );
  }

  // Check if we have any data at all
  const hasData = currentMetrics && (
    currentMetrics.adr !== null || 
    currentMetrics.occupancy !== null || 
    currentMetrics.revpar !== null
  );

  if (!hasData && performanceData && performanceData.length > 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <ShowChartIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Market Performance Analytics
          </Typography>
        </Box>
        <Alert severity="info">
          No performance data is currently available for this market. Please check back later or contact support if you believe this is an error.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChartIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Market Performance Analytics
          </Typography>
        </Box>
        
        <ToggleButtonGroup
          value={selectedPeriod}
          exclusive
          onChange={handlePeriodChange}
          size="small"
        >
          <ToggleButton value="current">Current</ToggleButton>
          <ToggleButton value="3_month">3 Month</ToggleButton>
          <ToggleButton value="12_month">12 Month</ToggleButton>
          <ToggleButton value="ytd">YTD</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Average Daily Rate (ADR)"
            value={currentMetrics?.adr ?? null}
            change={currentMetrics?.adr_change ?? null}
            format="currency"
            loading={isLoadingPerformance}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Occupancy Rate"
            value={currentMetrics?.occupancy ?? null}
            change={currentMetrics?.occupancy_change ?? null}
            format="percentage"
            loading={isLoadingPerformance}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Revenue per Available Room (RevPAR)"
            value={currentMetrics?.revpar ?? null}
            change={currentMetrics?.revpar_change ?? null}
            format="currency"
            loading={isLoadingPerformance}
          />
        </Grid>
      </Grid>

      {/* Performance Trend Chart */}
      <Box sx={{ mb: 4 }}>
        <PerformanceTrendChart
          data={processedTimeSeriesData}
          loading={isLoadingTimeSeries}
          title={`Performance Trends - ${marketName}`}
          timeRange={selectedTimeRange}
          onTimeRangeChange={setSelectedTimeRange}
          allClassificationsData={timeSeriesData}
        />
      </Box>

      {/* Row 1: Rankings and Hotel Category - 33% width each */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Current Percentile Rankings */}
        <Grid item xs={12} md={4}>
          <CurrentPercentileRankings
            data={rankingsData?.current || null}
            loading={isLoadingRankings}
            title="Current Market Percentile Rankings"
          />
        </Grid>

        {/* Percentile Rankings Over Time */}
        <Grid item xs={12} md={4}>
          <PercentileRankingsOverTime
            data={rankingsData?.timeseries || []}
            loading={isLoadingRankings}
            title="Percentile Rankings Over Time"
          />
        </Grid>

        {/* Hotel Category Breakdown */}
        <Grid item xs={12} md={4}>
          <HotelCategoryChart
            data={categoryChartData}
            loading={isLoadingPerformance}
            title={`Performance by Hotel Category - ${getPeriodLabel(selectedPeriod)}`}
          />
        </Grid>
      </Grid>

      {/* Airport Charts - Only show for United States markets - 33% width each */}
      {marketCountry === 'United States' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <AirportTrafficChart
              data={airportTrafficData?.airports || []}
              startYear={airportTrafficData?.startYear || null}
              startMonth={airportTrafficData?.startMonth || null}
              endYear={airportTrafficData?.endYear || null}
              endMonth={airportTrafficData?.endMonth || null}
              loading={isLoadingAirportTraffic}
              title="Last 12 Months Airport Passenger Traffic"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <AirportArrivalsByCountryChart
              data={airportArrivalsByCountryData?.countries || []}
              startYear={airportArrivalsByCountryData?.startYear || null}
              startMonth={airportArrivalsByCountryData?.startMonth || null}
              endYear={airportArrivalsByCountryData?.endYear || null}
              endMonth={airportArrivalsByCountryData?.endMonth || null}
              marketCountry={marketCountry || null}
              loading={isLoadingAirportArrivalsByCountry}
              title="Last 12 Months Airport Arrivals by Country"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <MonthlyPassengerChart
              data={monthlyPassengersData?.monthlyData || []}
              startYear={monthlyPassengersData?.startYear || null}
              startMonth={monthlyPassengersData?.startMonth || null}
              endYear={monthlyPassengersData?.endYear || null}
              endMonth={monthlyPassengersData?.endMonth || null}
              loading={isLoadingMonthlyPassengers}
              title="Last 12 Months Arriving Passengers by Month"
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

// Helper function to get period label
function getPeriodLabel(period: PeriodType): string {
  switch (period) {
    case 'current':
      return 'Current Period';
    case '3_month':
      return '3-Month Average';
    case '12_month':
      return '12-Month Average';
    case 'ytd':
      return 'Year-to-Date';
    default:
      return '';
  }
}